'use client';

import { useState } from 'react';
import Image from 'next/image';
import styles from './TechIcon.module.css';

interface TechIconProps {
  technology: {
    name: string;
    logo: string;
    logoFallbacks?: string[];
  };
  width: number;
  height: number;
  className?: string;
}

/**
 * TechIcon component that handles image loading failures with fallbacks
 * It tries the main logo URL first, then attempts each fallback in order if the image fails to load
 */
export default function TechIcon({ technology, width, height, className = '' }: TechIconProps) {
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  
  // Combine main logo with fallbacks into a single array
  const allLogos = [technology.logo, ...(technology.logoFallbacks || [])];
  
  // Only show fallback text if we've tried all possible image sources
  const showFallbackText = hasError && currentLogoIndex >= allLogos.length - 1;
  
  // Handle image loading error by trying the next fallback
  const handleImageError = () => {
    if (currentLogoIndex < allLogos.length - 1) {
      setCurrentLogoIndex(currentLogoIndex + 1);
    } else {
      setHasError(true);
    }
  };

  // If we've tried all logo options and they all failed, show fallback text
  if (showFallbackText) {
    return (
      <div 
        className={`${styles.fallbackContainer} ${className}`}
        style={{
          width,
          height,
          fontSize: `${Math.min(width, height) * 0.4}px`,
        }}
      >
        {technology.name.substring(0, 2)}
      </div>
    );
  }

  // Otherwise, try to load the current image
  return (
    <Image
      src={allLogos[currentLogoIndex]}
      alt={`${technology.name} logo`}
      width={width}
      height={height}
      className={className}
      onError={handleImageError}
      priority={currentLogoIndex === 0} // Only prioritize the first attempt
    />
  );
}